// Thin API client. Auth uses a token getter populated by App.jsx via
// setTokenGetter — Clerk's getToken() is registered on sign-in and
// cleared on sign-out.

import { queueEntry, isNetworkError } from "./offline.js";

// Token getter — populated by App.jsx via setTokenGetter(getToken) after
// Clerk confirms sign-in. Async because Clerk's getToken() refreshes JWTs
// transparently when needed.
let _getToken = null;
export function setTokenGetter(fn) {
  _getToken = typeof fn === "function" ? fn : null;
}

// App.jsx registers a handler here so any 401 from any request is routed back
// to SignIn, instead of leaving the user stranded with a red "Unauthorized"
// banner on whichever screen they were on. Real-world trigger: an admin
// deactivates a user mid-session, or a deploy invalidates stored credentials.
let unauthorizedHandler = null;
export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = typeof fn === "function" ? fn : null;
}
function handleUnauthorized() {
  // With Clerk, session state is managed by ClerkProvider — we don't clear
  // tokens here. The App's unauthorizedHandler resets React state so the
  // Clerk SignIn screen shows up.
  if (unauthorizedHandler) {
    try {
      unauthorizedHandler();
    } catch {
      // Swallow — the handler is just UI plumbing; don't mask the 401.
    }
  }
}

// Shared helpers for authenticated blob downloads (preview + download).
// Pulled out so Submit.jsx and SupervisorReview.jsx don't duplicate the
// header-attach / error-shape / filename-parse logic.
async function authHeaders() {
  if (!_getToken) return {};
  try {
    const token = await _getToken();
    if (token) return { authorization: `Bearer ${token}` };
  } catch {
    // getToken() can throw if the session is gone; treat as unauthenticated.
  }
  return {};
}

function filenameOf(res, fallback) {
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  return m?.[1] || fallback;
}

async function httpError(res) {
  let msg = `HTTP ${res.status}`;
  try {
    const j = await res.json();
    if (j.error) msg = j.error;
  } catch {}
  const err = new Error(msg);
  err.status = res.status;
  if (res.status === 401) handleUnauthorized();
  return err;
}

// Trigger a browser download of a fetched blob. Uses a temporary anchor
// element and object URL — works on desktop and iOS/Android Safari without
// popup-blocker issues (unlike window.open("about:blank") + redirect).
export function downloadBlob({ blob, filename }) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  // Delay cleanup to avoid revoking the URL before the browser starts
  // the download — iOS Safari and some Android browsers need time.
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 60_000);
}

async function request(path, opts = {}) {
  const headers = {
    "content-type": "application/json",
    ...(await authHeaders()),
    ...(opts.headers || {}),
  };
  const res = await fetch(path, { ...opts, headers });
  if (res.status === 204) return null;
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const body = await res.json();
    if (!res.ok) {
      const err = new Error(body.error || `HTTP ${res.status}`);
      err.status = res.status;
      err.details = body.details;
      if (res.status === 401) handleUnauthorized();
      throw err;
    }
    return body;
  }
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    if (res.status === 401) handleUnauthorized();
    throw err;
  }
  return res;
}

export const api = {
  async ensureUser({ email, fullName, role = "worker", supervisorId = null }) {
    return request("/api/dev/ensure-user", {
      method: "POST",
      body: JSON.stringify({ email, fullName, role, supervisorId }),
    });
  },
  me: () => request("/api/me"),
  listEntries: (year, month) =>
    request(`/api/entries?year=${year}&month=${month}`),
  // Create a trip. If the network is unreachable (offline, DNS fail, worker
  // phone on no-signal), stash the payload in IndexedDB and return an
  // optimistic record so the UI can render a "pending sync" row immediately.
  // A server-side 4xx/5xx is NOT swallowed — those bubble up like before.
  async createEntry(entry) {
    try {
      return await request("/api/entries", {
        method: "POST",
        body: JSON.stringify(entry),
      });
    } catch (e) {
      if (isNetworkError(e)) {
        const queued = await queueEntry(entry);
        queued._offline = true; // hint for the caller / toasts
        return queued;
      }
      throw e;
    }
  },
  updateEntry: (id, patch) =>
    request(`/api/entries/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  deleteEntry: (id) => request(`/api/entries/${id}`, { method: "DELETE" }),
  previewUrl: (year, month) =>
    `/api/submissions/preview?year=${year}&month=${month}`,
  // Authenticated fetch-and-download for the live preview. Plain <a href>
  // links can't attach the Authorization header, so the preview endpoint
  // 401s in a fresh tab. Instead, fetch the stream with the token, then
  // trigger a browser download from the blob. See downloadBlob() below.
  async previewMonth(year, month) {
    const res = await fetch(
      `/api/submissions/preview?year=${year}&month=${month}&_=${Date.now()}`,
      { headers: await authHeaders() }
    );
    if (!res.ok) throw await httpError(res);
    return { blob: await res.blob(), filename: filenameOf(res, `preview-${year}-${String(month).padStart(2, "0")}.xlsx`) };
  },
  submit: ({ year, month, employeeSignature }) =>
    request("/api/submissions", {
      method: "POST",
      body: JSON.stringify({ year, month, employeeSignature }),
    }),
  approve: (submissionId, supervisorSignature) =>
    request(`/api/submissions/${submissionId}/approve`, {
      method: "POST",
      body: JSON.stringify({ supervisorSignature }),
    }),
  // Return a submission to the worker for edits. `reason` is mandatory
  // (3–1000 chars) — the server rejects empty/short reasons because the
  // worker needs to know what to fix. The month becomes editable again
  // as soon as the row flips to 'returned'.
  returnSubmission: (submissionId, reason) =>
    request(`/api/submissions/${submissionId}/return`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  listPending: ({ history = false } = {}) =>
    request(`/api/submissions/pending${history ? "?history=1" : ""}`),
  getSubmission: (id) => request(`/api/submissions/${id}`),
  // Chronological audit trail for a single submission — "who did what when",
  // scoped server-side (worker sees their own, supervisor sees reports they
  // own, admin sees all). Used to render the timeline card under the claim
  // summary on Submit.jsx and SupervisorReview.jsx.
  getSubmissionTimeline: (id) => request(`/api/submissions/${id}/timeline`),
  downloadUrl: (submissionId) => `/api/submissions/${submissionId}/download`,
  // Authenticated fetch-and-download for the final/preview artifact on a
  // saved submission. Same reasoning as previewMonth() above — Authorization
  // header cannot be set on a plain anchor link.
  async downloadSubmission(submissionId) {
    const res = await fetch(
      `/api/submissions/${submissionId}/download?_=${Date.now()}`,
      { headers: await authHeaders() }
    );
    if (!res.ok) throw await httpError(res);
    return { blob: await res.blob(), filename: filenameOf(res, `endeavor-mileage-${submissionId}.xlsx`) };
  },
  // admin
  adminListUsers: () => request("/api/admin/users"),
  adminCreateUser: (payload) =>
    request("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  adminUpdateUser: (id, patch) =>
    request(`/api/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  adminGetConfig: () => request("/api/admin/config"),
  adminSetConfig: (patch) =>
    request("/api/admin/config", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  // Paginated audit log. Filters are optional — if caller passes `entityType`
  // or `action`, server-side WHERE narrows the result set. `before`/`after`
  // JSON blobs are already parsed on the server.
  adminListAudit: ({
    limit = 100,
    offset = 0,
    entityType,
    action,
    actorEmail,
  } = {}) => {
    const qp = new URLSearchParams();
    qp.set("limit", String(limit));
    qp.set("offset", String(offset));
    if (entityType) qp.set("entityType", entityType);
    if (action) qp.set("action", action);
    if (actorEmail) qp.set("actorEmail", actorEmail);
    return request(`/api/admin/audit?${qp.toString()}`);
  },
  // Audit log CSV. Mirrors adminListAudit filters, streams as attachment.
  // Used by AuditTab's "Export CSV" button for payroll/compliance reviews.
  async adminExportAuditCsv({ entityType, action, actorEmail } = {}) {
    const qp = new URLSearchParams();
    if (entityType) qp.set("entityType", entityType);
    if (action) qp.set("action", action);
    if (actorEmail) qp.set("actorEmail", actorEmail);
    const headers = await authHeaders();
    const res = await fetch(`/api/admin/audit.csv?${qp.toString()}`, { headers });
    if (!res.ok) throw await httpError(res);
    return {
      blob: await res.blob(),
      filename: filenameOf(res, "endeavor-audit.csv"),
    };
  },
  // Construct a CSV export URL. Caller opens this in a new tab so the browser
  // handles the download with the session cookie / auth header. Because we
  // still use the x-endeavor-user header stub in v0, we fall back to wiring
  // it through a fetch + Blob for downloads that need authentication.
  async adminExportCsv({ year, month, detail = false, status = "approved" } = {}) {
    const qp = new URLSearchParams();
    qp.set("year", String(year));
    if (month) qp.set("month", String(month));
    if (detail) qp.set("detail", "1");
    if (status) qp.set("status", status);
    const headers = await authHeaders();
    const res = await fetch(`/api/admin/export.csv?${qp.toString()}`, { headers });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try {
        const j = await res.json();
        if (j.error) msg = j.error;
      } catch {}
      const err = new Error(msg);
      err.status = res.status;
      if (res.status === 401) handleUnauthorized();
      throw err;
    }
    const blob = await res.blob();
    const filename =
      (res.headers.get("content-disposition") || "").match(/filename="([^"]+)"/)?.[1]
      || `endeavor-mileage-${year}${month ? `-${String(month).padStart(2, "0")}` : ""}.csv`;
    return { blob, filename };
  },
};
